import axios from 'axios';

// Use the default axios instance which has interceptors configured
// The base URL is already configured in AuthContext
const api = axios;

const inFlightRequests = new Map<string, Promise<any>>();

const dedupeRequest = <T>(key: string, requestFn: () => Promise<T>): Promise<T> => {
  const existing = inFlightRequests.get(key);
  if (existing) {
    return existing as Promise<T>;
  }
  
  const promise = requestFn().finally(() => {
    inFlightRequests.delete(key);
  });
  
  inFlightRequests.set(key, promise);
  return promise;
};

export interface Account {
  account_id: string;
  name: string;
  type: string;
  subtype: string;
  institution_name?: string | null;
  balances: {
    available: number | null;
    current: number | null;
    limit: number | null;
  };
}

export interface Transaction {
  transaction_id: string;
  account_id: string;
  amount: number;
  date: string;
  name: string;
  merchant_name?: string;
  logo_url?: string;
  category?: string[]; // Legacy field - kept for backward compatibility
  personal_finance_category?: {
    primary: string;
    detailed: string;
    confidence_level: string;
  }; // New Plaid field - object format
  account_owner?: string;
}

export interface LinkTokenResponse {
  link_token: string;
  expiration: string;
  request_id: string;
}

export interface ExchangeTokenResponse {
  access_token: string;
  item_id: string;
  request_id: string;
}

export const plaidService = {
  createLinkToken: async (): Promise<LinkTokenResponse> => {
    const response = await api.post('/plaid/create_link_token');
    return response.data;
  },

  exchangeToken: async (publicToken: string): Promise<ExchangeTokenResponse> => {
    const response = await api.post('/plaid/exchange_token', {
      public_token: publicToken,
    });
    return response.data;
  },

  getAccounts: async (): Promise<{ accounts: Account[] }> => {
    const response = await api.get('/plaid/accounts');
    return response.data;
  },

  getTransactions: async (
    startDate?: string,
    endDate?: string
  ): Promise<{ transactions: Transaction[] }> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const response = await api.get(`/plaid/transactions?${params.toString()}`);
    return response.data;
  },

  syncData: async (): Promise<{ message: string; accounts_synced: number; transactions_synced: number }> => {
    const response = await api.post('/plaid/sync');
    return response.data;
  },

  getSpending: (
    startDate?: string,
    endDate?: string,
    accountId?: string,
    limit?: number,
    offset?: number
  ): Promise<{ transactions: Transaction[] }> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (accountId) params.append('account_id', accountId);
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    
    const query = params.toString();
    const key = `spending-${query}`;
    return dedupeRequest(key, async () => {
      const response = await api.get(`/plaid/spending${query ? `?${query}` : ''}`);
      return response.data;
    });
  },

  getSpending2: (
    page: number = 1,
    pageSize: number = 15,
    startDate?: string,
    endDate?: string,
    accountId?: string,
  ): Promise<{ transactions: Transaction[]; total: number; page: number; page_size: number }> => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (accountId) params.append('account_id', accountId);

    const query = params.toString();
    const key = `spending2-${query}`;
    return dedupeRequest(key, async () => {
      const response = await api.get(`/plaid/spending2?${query}`);
      return response.data;
    });
  },

  getIncome2: (
    page: number = 1,
    pageSize: number = 25,
    startDate?: string,
    endDate?: string,
    accountId?: string,
  ): Promise<{ transactions: Transaction[]; total: number; page: number; page_size: number }> => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (accountId) params.append('account_id', accountId);

    const query = params.toString();
    const key = `income2-${query}`;
    return dedupeRequest(key, async () => {
      const response = await api.get(`/plaid/income2?${query}`);
      return response.data;
    });
  },

  getIncome: (
    startDate?: string,
    endDate?: string,
    accountId?: string,
    limit?: number,
    offset?: number
  ): Promise<{ transactions: Transaction[] }> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (accountId) params.append('account_id', accountId);
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    
    const query = params.toString();
    const key = `income-${query}`;
    return dedupeRequest(key, async () => {
      const response = await api.get(`/plaid/income${query ? `?${query}` : ''}`);
      return response.data;
    });
  },

  getWeeklyReport: async (): Promise<WeeklyReport> => {
    const response = await api.get('/reports/weekly');
    return response.data;
  },

  getNetWorthTrend: async (period?: string): Promise<NetWorthTrendResponse> => {
    const params = period ? `?period=${period}` : '';
    const response = await api.get(`/balance-snapshots/net-worth${params}`);
    return response.data;
  },

  // Manual accounts (real estate, etc.)
  createManualAccount: async (data: CreateManualAccountRequest): Promise<ManualAccount> => {
    const response = await api.post('/accounts/manual', data);
    return response.data;
  },

  getManualAccounts: async (): Promise<ManualAccount[]> => {
    const response = await api.get('/accounts/manual');
    return response.data;
  },

  updateManualAccount: async (id: number, data: Partial<CreateManualAccountRequest>): Promise<ManualAccount> => {
    const response = await api.put(`/accounts/manual/${id}`, data);
    return response.data;
  },

  deleteManualAccount: async (id: number): Promise<void> => {
    await api.delete(`/accounts/manual/${id}`);
  },

  getRecurring: async (): Promise<RecurringResponse> => {
    const response = await api.get('/plaid/recurring');
    return response.data;
  },

  getYearlySalary: async (): Promise<any> => {
    const response = await api.get('/plaid/salary/yearly');
    return response.data;
  },

  getMonthlySpend: async (): Promise<MonthlySpendResponse> => {
    const response = await api.get('/plaid/spend/monthly');
    return response.data;
  },

  getNetIncomeLast3Months: async (): Promise<NetIncomeLast3MonthsResponse> => {
    const response = await api.get('/plaid/net-income/last-3-months');
    return response.data;
  },

  getSpendingByCategory: (
    startDate: string,
    endDate: string,
  ): Promise<SpendingByCategoryResponse> => {
    const params = new URLSearchParams();
    params.append('start_date', startDate);
    params.append('end_date', endDate);
    const query = params.toString();
    const key = `spending-by-category-${query}`;
    return dedupeRequest(key, async () => {
      const response = await api.get(`/plaid/spending/by-category?${query}`);
      return response.data;
    });
  },
};

export interface SpendingByCategoryItem {
  category: string;
  total: number;
  count: number;
  transactions: Transaction[];
}

export interface SpendingByCategoryResponse {
  categories: SpendingByCategoryItem[];
  total: number;
  count: number;
}

export interface NetIncomeMonth {
  month: string;
  income: number;
  spending: number;
  net_income: number;
}

export interface NetIncomeLast3MonthsResponse {
  start_date: string;
  end_date: string;
  months_in_window: number;
  total_income: number;
  total_spending: number;
  net_income: number;
  monthly_average_net_income: number;
  months: NetIncomeMonth[];
}

export interface MonthlySpendTransaction {
  transaction_id: string;
  date: string;
  amount: number;
  name: string;
  detailed: string;
}

export interface MonthlySpendCategory {
  category: string;
  monthly_average: number;
  total_amount: number;
  months_observed: number;
  transaction_count: number;
  transactions: MonthlySpendTransaction[];
  user_verified: boolean;
}

export interface MonthlySpendResponse {
  start_date: string;
  end_date: string;
  total_transaction_count: number;
  categories: MonthlySpendCategory[];
}

export interface NetWorthDataPoint {
  date: string;
  net_worth: number;
  assets: number;
  liabilities: number;
}

export interface NetWorthTrendResponse {
  period: string;
  start_date: string;
  end_date: string;
  granularity: string;
  net_worth_trend: NetWorthDataPoint[];
}

export interface CreateManualAccountRequest {
  name: string;
  type: string;
  subtype: string;
  current_balance: number;
  iso_currency_code: string;
}

export interface ManualAccount {
  id: number;
  name: string;
  type: string;
  subtype: string;
  current_balance: number;
  iso_currency_code: string;
  created_at: string;
  updated_at: string;
}

export interface RecurringStream {
  stream_id: string;
  description: string;
  merchant_name: string | null;
  frequency: string;
  average_amount: { amount: number };
  last_amount: { amount: number };
  is_active: boolean;
  status: string;
  personal_finance_category: string;
  first_date: string;
  last_date: string;
}

export interface RecurringResponse {
  inflow_streams: RecurringStream[];
  outflow_streams: RecurringStream[];
  updated_at: string;
}

export interface WeeklyReport {
  period: {
    this_week: { start: string; end: string };
    last_week: { start: string; end: string };
  };
  transaction_counts: {
    this_week: number;
    last_week: number;
  };
  totals: {
    this_week_spent: number;
    last_week_spent: number;
    this_week_income: number;
    last_week_income: number;
  };
  report: string;
}

// AI service functions
export const aiService = {
  categorizeTransaction: async (transaction: Transaction) => {
    const response = await api.post('/ai/categorize', { transaction });
    return response.data;
  },
  
  getInsights: async () => {
    const response = await api.get('/ai/insights');
    return response.data;
  },
  
  searchTransactions: async (query: string) => {
    const response = await api.post('/ai/search', { query });
    return response.data;
  }
};

export default plaidService;
